/**
 * AuthService — Manages Google Sign-In, session state, and user profile.
 *
 * Uses Google's "Sign In With Google" (GSI) client library loaded from a
 * <script> tag. After the user signs in, the Google credential (ID token)
 * is sent to our backend which verifies it, creates a session, and sets
 * a cookie. From that point the cookie handles auth automatically.
 *
 * Sessions last 1 year and survive code deployments because they live
 * in the database, not in memory or localStorage.
 */

// Google's GSI types (minimal)
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            auto_select?: boolean;
          }) => void;
          prompt: (callback?: (notification: { isNotDisplayed: () => boolean; isSkippedMoment: () => boolean }) => void) => void;
          renderButton: (
            parent: HTMLElement,
            options: {
              type?: string;
              theme?: string;
              size?: string;
              shape?: string;
              text?: string;
              width?: number;
            }
          ) => void;
          revoke: (email: string, callback: () => void) => void;
          cancel: () => void;
        };
      };
    };
  }
}

export interface AuthUser {
  id: string;
  email: string;
  display_name: string | null;
  picture_url: string | null;
}

type AuthChangeCallback = (user: AuthUser | null) => void;

// The Google Client ID — loaded from a <meta> tag or environment variable injected at build time
const GOOGLE_CLIENT_ID_META = document.querySelector<HTMLMetaElement>('meta[name="google-client-id"]');
const GOOGLE_CLIENT_ID = GOOGLE_CLIENT_ID_META?.content || '';

/** Auth API base URL: from /config.json (runtime) or VITE_AUTH_API_BASE (build). Empty = same origin. */
const BUILD_AUTH_API_BASE: string = import.meta.env.VITE_AUTH_API_BASE ?? '';

class _AuthService {
  private _user: AuthUser | null = null;
  private _ready = false;
  private _gsiLoaded = false;
  private _listeners: AuthChangeCallback[] = [];
  private _needsDisplayName = false;
  /** Resolved at init from config.json or build env. No trailing slash. */
  private _authApiBase = '';

  /** Current user (null if not logged in) */
  get user(): AuthUser | null {
    return this._user;
  }

  get isLoggedIn(): boolean {
    return this._user !== null;
  }

  get needsDisplayName(): boolean {
    return this._needsDisplayName;
  }

  get ready(): boolean {
    return this._ready;
  }

  /** API base URL for save sync and other endpoints (no trailing slash). */
  get authApiBase(): string {
    return this._authApiBase;
  }

  /** Subscribe to auth state changes */
  onChange(cb: AuthChangeCallback): () => void {
    this._listeners.push(cb);
    return () => {
      this._listeners = this._listeners.filter((l) => l !== cb);
    };
  }

  private _notify(): void {
    for (const cb of this._listeners) {
      try { cb(this._user); } catch { /* ignore */ }
    }
  }

  // ─── Initialization ────────────────────────────────────────────────

  /** Call once on game boot. Resolves API base, checks session, loads GSI. */
  async init(): Promise<void> {
    if (this._ready) return;

    // Resolve auth API base: optional /config.json (for production FTP/cPanel) overrides build-time env
    await this._resolveAuthApiBase();

    // Check for existing session (cookie-based, survives deploys)
    await this._fetchMe();

    // Load Google Sign-In library
    await this._loadGSI();

    this._ready = true;
    console.log('[AuthService] Ready. User:', this._user?.display_name || 'not logged in');
    // Start save sync when logged in (one-time pull or upload local)
    import('./SaveSyncService').then((m) => m.SaveSyncService.init()).catch(() => {});
  }

  /** Load config.json from same origin; if authApiBase is set, use it. Otherwise use build-time value. */
  private async _resolveAuthApiBase(): Promise<void> {
    try {
      const res = await fetch('/config.json', { cache: 'no-store' });
      if (res.ok) {
        const data = (await res.json()) as { authApiBase?: string };
        const base = typeof data.authApiBase === 'string' ? data.authApiBase.trim().replace(/\/$/, '') : '';
        if (base) {
          this._authApiBase = base;
          console.log('[AuthService] Using auth API from config.json:', base);
          return;
        }
      }
    } catch {
      // No config.json or invalid — use build-time default
    }
    this._authApiBase = BUILD_AUTH_API_BASE ? BUILD_AUTH_API_BASE.replace(/\/$/, '') : '';
  }

  // ─── Google Sign-In ────────────────────────────────────────────────

  /** Trigger Google sign-in popup. Returns the authenticated user. */
  signInWithGoogle(): Promise<AuthUser | null> {
    return new Promise((resolve) => {
      if (!window.google || !GOOGLE_CLIENT_ID) {
        console.warn('[AuthService] Google Sign-In not available');
        resolve(null);
        return;
      }

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response) => {
          const user = await this._handleGoogleCredential(response.credential);
          resolve(user);
        },
      });

      // Use the One Tap prompt
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          // One Tap was blocked (e.g. user dismissed before) — fall back to button click
          // We'll handle this with a rendered button in the UI instead
          console.log('[AuthService] One Tap not shown, using button fallback');
          resolve(null);
        }
      });
    });
  }

  /** Render a Google Sign-In button into the given DOM element */
  async renderGoogleButton(container: HTMLElement): Promise<void> {
    // Make sure GSI is loaded (in case init() hasn't finished yet)
    if (!this._gsiLoaded) {
      console.log('[AuthService] GSI not loaded yet, loading now...');
      await this._loadGSI();
    }

    if (!window.google?.accounts?.id) {
      console.warn('[AuthService] Cannot render Google button — GSI library not available');
      container.innerHTML = '<p style="color:#ff4444;font-size:13px;">Google Sign-In unavailable</p>';
      return;
    }
    if (!GOOGLE_CLIENT_ID) {
      console.warn('[AuthService] Cannot render Google button — no client ID configured');
      container.innerHTML = '<p style="color:#ff4444;font-size:13px;">Sign-in not configured</p>';
      return;
    }

    console.log('[AuthService] Rendering Google button...');

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: async (response) => {
        console.log('[AuthService] Google callback fired, sending credential to server...');
        await this._handleGoogleCredential(response.credential);
      },
    });

    window.google.accounts.id.renderButton(container, {
      type: 'standard',
      theme: 'filled_black',
      size: 'large',
      shape: 'pill',
      text: 'signin_with',
      width: 280,
    });
  }

  /** Process the credential from Google */
  private async _handleGoogleCredential(credential: string): Promise<AuthUser | null> {
    this._notifyLoading(true);
    try {
      console.log('[AuthService] POSTing credential to /api/auth/google.php ...');
      const res = await fetch(`${this._authApiBase}/api/auth/google.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential }),
        credentials: 'include',
      });

      if (!res.ok) {
        const errorBody = await res.text();
        console.error('[AuthService] Google auth failed:', res.status, errorBody);
        this._notifyLoading(false);
        this._notifyError('Sign-in failed. Please try again.');
        return null;
      }

      const data = await res.json();
      console.log('[AuthService] Auth success:', data.user?.email, 'needs_display_name:', data.needs_display_name);
      this._notifyLoading(false);
      this._user = data.user;
      this._needsDisplayName = data.needs_display_name;
      this._notify();
      return this._user;
    } catch (err) {
      console.error('[AuthService] Google auth error:', err);
      this._notifyLoading(false);
      this._notifyError('Could not reach server. Is it running?');
      return null;
    }
  }

  // ─── Loading listeners ──────────────────────────────────────────────
  private _loadingListeners: ((loading: boolean) => void)[] = [];

  /** Subscribe to loading state changes (for showing spinner in UI) */
  onLoading(cb: (loading: boolean) => void): () => void {
    this._loadingListeners.push(cb);
    return () => {
      this._loadingListeners = this._loadingListeners.filter((l) => l !== cb);
    };
  }

  private _notifyLoading(loading: boolean): void {
    for (const cb of this._loadingListeners) {
      try { cb(loading); } catch { /* ignore */ }
    }
  }

  // ─── Error listeners ───────────────────────────────────────────────
  private _errorListeners: ((msg: string) => void)[] = [];

  /** Subscribe to auth errors (for showing in UI) */
  onError(cb: (msg: string) => void): () => void {
    this._errorListeners.push(cb);
    return () => {
      this._errorListeners = this._errorListeners.filter((l) => l !== cb);
    };
  }

  private _notifyError(msg: string): void {
    for (const cb of this._errorListeners) {
      try { cb(msg); } catch { /* ignore */ }
    }
  }

  // ─── Display Name ──────────────────────────────────────────────────

  /** Set or update the user's display name */
  async setDisplayName(displayName: string): Promise<boolean> {
    try {
      const res = await fetch(`${this._authApiBase}/api/auth/display-name.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ display_name: displayName }),
        credentials: 'include',
      });

      if (!res.ok) {
        const data = await res.json();
        console.error('[AuthService] Set display name failed:', data.error);
        return false;
      }

      const data = await res.json();
      if (this._user) {
        this._user.display_name = data.display_name;
        this._needsDisplayName = false;
        this._notify();
      }
      return true;
    } catch (err) {
      console.error('[AuthService] Set display name error:', err);
      return false;
    }
  }

  // ─── Logout ────────────────────────────────────────────────────────

  async logout(): Promise<void> {
    try {
      await fetch(`${this._authApiBase}/api/auth/logout.php`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch { /* ignore */ }

    // Revoke Google session too (only if GSI was initialized)
    if (this._gsiLoaded && window.google?.accounts?.id && this._user?.email) {
      try {
        window.google.accounts.id.revoke(this._user.email, () => {});
      } catch { /* ignore */ }
    }

    this._user = null;
    this._needsDisplayName = false;
    this._notify();
  }

  // ─── Internal ──────────────────────────────────────────────────────

  /** Check for existing session via cookie */
  private async _fetchMe(): Promise<void> {
    try {
      const res = await fetch(`${this._authApiBase}/api/auth/me.php`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        this._user = data.user || null;
        if (this._user && !this._user.display_name) {
          this._needsDisplayName = true;
        }
      }
    } catch {
      // Server not available — that's fine, user just won't be logged in
      console.log('[AuthService] Server not reachable, continuing without auth');
    }
  }

  /** Load the Google Sign-In library (gsi client) */
  private _loadGSI(): Promise<void> {
    if (this._gsiLoaded || !GOOGLE_CLIENT_ID) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      if (window.google?.accounts?.id) {
        this._gsiLoaded = true;
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        this._gsiLoaded = true;
        resolve();
      };
      script.onerror = () => {
        console.warn('[AuthService] Failed to load Google Sign-In library');
        resolve(); // Don't block the game
      };
      document.head.appendChild(script);
    });
  }
}

/** Singleton AuthService */
export const AuthService = new _AuthService();
